import { ErrorAlert } from '@/components/_ui/ErrorAlert'
import type { CosmicBannerDataType } from '@/lib/cms/cosmic/types/CosmicProductsBannerType'
import type { CMSError } from '@/lib/cms/types/CMSError'
import useParseLinkUrlWithLocale from '@/lib/hooks/routing/useParseLinkUrlWithLocale'
import { trackShoSelectProduct } from '@/lib/services/mixpanel/events/productEvents'
import { trackBannerClick } from '@/lib/services/mixpanel/events/bannerEvents'
import useGlobalStore from '@/store/useGlobalStore'
import { useEventLocation } from '@/lib/services/mixpanel/helpers/getEventLocation'
import Image from 'next/image'
import { isProductPageUrl } from '@/lib/services/mixpanel/helpers/isProductPageUrl'

type Props = {
  productsBannerImageData: CosmicBannerDataType | { error: CMSError } | null
}

export const ProductsBanner = ({ productsBannerImageData }: Props) => {
  const { parseLinkUrlWithLocale } = useParseLinkUrlWithLocale()
  const {
    user: { customerType },
    app: { marketExceptions },
  } = useGlobalStore()
  const eventLocation = useEventLocation()

  const handleBannerClick = async () => {
    if (productsBannerImageData && !('error' in productsBannerImageData)) {
      const bannerName =
        productsBannerImageData.bannerImageDesktop ||
        productsBannerImageData.bannerImageMobile

      await trackBannerClick({
        eventLocation,
        topBannerName: bannerName.split('/').pop() || '',
        linkUrl:
          productsBannerImageData.bannerImageDesktop ||
          productsBannerImageData.bannerImageMobile,
      }).catch((error) => {
        console.error('Banner click tracking failed:', error)
      })

      if (isProductPageUrl(productsBannerImageData.urlPath)) {
        const urlParts = productsBannerImageData.urlPath.split('/')
        const productSlug = urlParts.at(-1)?.split('?')[0] || ''
        const sku = productsBannerImageData.urlPath.includes('?sku=')
          ? productsBannerImageData.urlPath.split('?sku=')[1]
          : ''

        const productForTracking = {
          name: productSlug.toUpperCase(),
          slug: productSlug,
          description: productsBannerImageData.altText,
          variations: [
            {
              sku,
              combinationName:
                productSlug.charAt(0).toUpperCase() + productSlug.slice(1),
              pricing: null,
              isDefault: true,
              isDisable: false,
              partsLabel: '',
            },
          ],
          attributes: [],
          availableFor: {
            customer: true,
            member: true,
            associate: true,
            employee: true,
          },
        }

        await trackShoSelectProduct({
          product: productForTracking,
          selectedVariation: productForTracking.variations[0],
          eventLocation,
          action: 'top_banner_clicked',
          customerType,
          currencyCode: marketExceptions?.currencyCode || 'USD',
          topBannerName: bannerName.split('/').pop() || '',
        }).catch((error) => {
          console.error('Banner product tracking failed:', error)
        })
      }
    }
  }

  if (!productsBannerImageData) return null
  if ('error' in productsBannerImageData) {
    return (
      <div className="w-full">
        <ErrorAlert
          error={{
            error: {
              message: productsBannerImageData.error.message,
              code: productsBannerImageData.error.code,
            },
          }}
        />
      </div>
    )
  }

  if (
    !productsBannerImageData.bannerImageDesktop ||
    !productsBannerImageData.bannerImageMobile
  ) {
    return null
  }

  return (
    <a
      href={parseLinkUrlWithLocale({
        url: productsBannerImageData.urlPath,
      })}
      onClick={handleBannerClick}
      className="max-w-[2048px] flex-1"
    >
      <Image
        src={productsBannerImageData.bannerImageDesktop}
        alt={productsBannerImageData.altText}
        fill
        sizes="100vw"
        className="!relative hidden sm:block"
        priority
      />

      <Image
        src={productsBannerImageData.bannerImageMobile}
        alt={productsBannerImageData.altText}
        fill
        sizes="100vw"
        className="!relative block sm:hidden"
        priority
      />
    </a>
  )
}
